import React, { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ExternalLink, Music2, UserRound, Disc, ChevronRight, Edit, Trash2, Plus, Camera } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { ResearchAssistantPanel } from "@/components/ResearchAssistantPanel"
import { ImageGallery, Lightbox, type GalleryImage } from "@/components/ImageGallery"
import { PhotosTab } from "@/components/PhotosTab"
import { RequestDeletionButton } from "@/components/RequestDeletionButton"
import { videoEmbedUrl } from "@/lib/videoEmbed"
import { formatGigDate, type GigDatePrecision } from "@/lib/gigDates"

type Image = { url: string; og_image_url: string; alt_text: string }

type Member = {
  id: number
  person_id: number
  person_name: string
  person_slug: string
  person_image: Image | null
  instruments: string[]
  joined_year: number | null
  left_year: number | null
  importance: number
  details: string | null
}

type BandCredit = {
  id: number
  role: string
  details: string | null
  person_id: number
  person_name: string
  person_slug: string
  person_image: Image | null
}

type Recording = {
  id: number
  title: string
  slug: string
  recording_type: string | null
  released_year: number | null
  highest_position: number | null
  weeks_in_chart: number | null
  record_label: string | null
  primary_image: Image | null
}

type GigAct = {
  id: number
  name: string
  slug: string | null
  type: string
  notes: string | null
  source: string | null
  is_subject: boolean
}

type GigShow = {
  date: string
  date_precision: GigDatePrecision
  end_date: string | null
  venue: { name: string; city: string; state: string; slug: string }
  acts: GigAct[]
  images: GalleryImage[]
}

type GigYear = {
  year: number | null
  shows: GigShow[]
}

type Video = { id: number; name: string; url: string; source: string }
type BandLink = { id: number; link_text: string; url: string }

type BandDetail = {
  id: number
  name: string
  slug: string
  content: string | null
  in_amh: boolean
  years_active: string | null
  home_town: string | null
  all_recordings_added: boolean
  members_count: number
  recordings_count: number
  gigs_count: number
  credits_count: number
  primary_image: Image | null
  images: GalleryImage[]
  members: Member[]
  recordings: Recording[]
  credits: BandCredit[]
  gigs: GigYear[]
  videos: Video[]
  links: BandLink[]
}

type Permissions = { can_edit: boolean; can_delete: boolean; can_request_deletion: boolean }
type Tab = "members" | "recordings" | "credits" | "gigs" | "videos" | "links" | "photos"

const ALL_TABS: { id: Tab; label: string }[] = [
  { id: "members",    label: "Members" },
  { id: "recordings", label: "Recordings" },
  { id: "credits",    label: "Credits" },
  { id: "gigs",       label: "Gigs" },
  { id: "videos",     label: "Videos" },
  { id: "links",      label: "Links" },
  { id: "photos",     label: "Photos" },
]

const AU_STATES = [ "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA" ]

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-14 text-center">
      <Music2 className="w-5 h-5 text-stone-300 dark:text-stone-700 mx-auto mb-2" />
      <p className="text-sm text-stone-400 dark:text-stone-500">{message}</p>
    </div>
  )
}

function GigsTab({ gigs, canEdit, returnTo }: { gigs: GigYear[]; canEdit: boolean; returnTo: string }) {
  const [ expandedYears, setExpandedYears ] = useState<Set<number | null>>(
    () => new Set(gigs.slice(-3).map((g) => g.year))
  )
  const [ stateFilter, setStateFilter ] = useState("")
  const [ gigLightbox, setGigLightbox ] = useState<{ images: GalleryImage[]; startIndex: number } | null>(null)

  const allStates = AU_STATES.filter((s) =>
    gigs.some((yr) => yr.shows.some((sh) => sh.venue.state === s))
  )
  const filtered = stateFilter
    ? gigs.map((yr) => ({ ...yr, shows: yr.shows.filter((sh) => sh.venue.state === stateFilter) })).filter((yr) => yr.shows.length > 0)
    : gigs
  const totalShows = filtered.reduce((n, yr) => n + yr.shows.length, 0)

  if (gigs.length === 0) return <EmptyState message="No gigs on record yet." />

  function toggle(year: number | null) {
    setExpandedYears((prev) => {
      const next = new Set(prev)
      next.has(year) ? next.delete(year) : next.add(year)
      return next
    })
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-stone-100 dark:border-stone-800">
        <select
          value={stateFilter}
          onChange={(e) => {
            setStateFilter(e.target.value)
            setExpandedYears(new Set(filtered.slice(0, 3).map((g) => g.year)))
          }}
          className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-xs text-stone-700 dark:text-stone-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors"
        >
          <option value="">All states</option>
          {allStates.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
          {totalShows} show{totalShows !== 1 ? "s" : ""} · {filtered.length} year{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="divide-y divide-stone-100 dark:divide-stone-800/60">
        {filtered.map(({ year, shows }) => {
          const open = expandedYears.has(year)
          return (
            <div key={year ?? "unknown"}>
              <button
                onClick={() => toggle(year)}
                className="w-full flex items-center gap-2 py-3 text-left hover:bg-stone-50 dark:hover:bg-stone-800/40 -mx-1 px-1 rounded transition-colors"
              >
                <ChevronRight className={`w-3.5 h-3.5 text-stone-400 shrink-0 transition-transform duration-150 ${open ? "rotate-90" : ""}`} />
                <span className="text-sm font-semibold text-stone-900 dark:text-white tabular-nums" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                  {year ?? "Year unknown"}
                </span>
                <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                  · {shows.length} show{shows.length !== 1 ? "s" : ""}
                </span>
              </button>

              {open && (
                <div className="mb-2 divide-y divide-stone-50 dark:divide-stone-800/40 ml-5">
                  {shows.map((show) => (
                    <div key={show.date} className="py-2.5 flex items-start gap-3">
                      <span className="text-xs text-stone-500 dark:text-stone-400 shrink-0 w-20 tabular-nums mt-0.5" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                        {formatGigDate(show.date, show.end_date, show.date_precision)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-3">
                          {show.images.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setGigLightbox({ images: show.images, startIndex: 0 })}
                              className="relative shrink-0 w-20 h-16 rounded overflow-hidden bg-stone-100 dark:bg-stone-800 hover:opacity-80 transition-opacity"
                              aria-label="View gig photo"
                            >
                              <img src={show.images[0].thumb_url ?? undefined} alt="Gig photo" className="w-full h-full object-cover" />
                              {show.images.length > 1 && (
                                <span className="absolute bottom-0.5 right-0.5 text-[9px] font-medium bg-black/60 text-white rounded px-1 leading-4">
                                  {show.images.length}
                                </span>
                              )}
                            </button>
                          )}
                          <div className="flex-1 min-w-0">
                            <Link
                              href={`/venues/${show.venue.slug}`}
                              className="text-sm font-medium text-stone-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 transition-colors leading-snug no-underline"
                              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                            >
                              {show.venue.name}
                            </Link>
                            <p className="text-xs text-stone-500 dark:text-stone-400" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                              {show.venue.city}, {show.venue.state}
                            </p>
                            {show.acts.length > 1 && (
                              <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                <span className="text-stone-400 dark:text-stone-500">Also appearing: </span>
                                {show.acts.filter((a) => !a.is_subject).map((a) => (
                                  a.slug
                                    ? <Link key={a.name} href={`/${a.type === "Band" ? "bands" : "people"}/${a.slug}`} className="hover:text-amber-600 dark:hover:text-amber-400 no-underline">{a.name}</Link>
                                    : <span key={a.name}>{a.name}</span>
                                )).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ", ", el], [])}
                              </p>
                            )}
                            {show.acts.find((a) => a.is_subject)?.notes && (
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5 italic">
                                {show.acts.find((a) => a.is_subject)!.notes}
                              </p>
                            )}
                            {show.acts.find((a) => a.is_subject)?.source && (
                              <p className="text-xs text-stone-400 dark:text-stone-500 mt-0.5">
                                Source: {show.acts.find((a) => a.is_subject)!.source}
                              </p>
                            )}
                            {canEdit && (() => {
                              const subjectAct = show.acts.find((a) => a.is_subject)
                              return subjectAct ? (
                                <div className="flex items-center gap-3 mt-1">
                                  <Link
                                    href={`/gigs/${subjectAct.id}/edit?return_to=${encodeURIComponent(returnTo)}`}
                                    className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                                  >
                                    <Edit className="w-3 h-3" /> Edit
                                  </Link>
                                  <Link
                                    href={`/admin/image_attachments/new?attachable_type=Gig&attachable_id=${subjectAct.id}&return_to=${encodeURIComponent(returnTo)}`}
                                    className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                                  >
                                    <Camera className="w-3 h-3" /> Photo
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (confirm("Delete this gig?")) {
                                        router.delete(`/gigs/${subjectAct.id}`, { data: { return_to: returnTo } })
                                      }
                                    }}
                                    className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" /> Delete
                                  </button>
                                </div>
                              ) : null
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {gigLightbox && (
        <Lightbox images={gigLightbox.images} startIndex={gigLightbox.startIndex} onClose={() => setGigLightbox(null)} />
      )}
    </div>
  )
}

export default function BandShow({ band, permissions }: { band: BandDetail; permissions: Permissions }) {

  const tabCounts: Record<Tab, number> = {
    members:    band.members.length,
    recordings: band.recordings.length,
    credits:    band.credits.length,
    gigs:       band.gigs_count,
    videos:     band.videos.length,
    links:      band.links.length,
    photos:     band.images.length,
  }
  const visibleTabs = ALL_TABS.filter((t) => tabCounts[t.id] > 0 || permissions.can_edit)
  const [ activeTab, setActiveTab ] = useState<Tab>(() => visibleTabs[0]?.id ?? "members")

  function handleDelete() {
    if (confirm(`Delete "${band.name}"? This cannot be undone.`)) {
      router.delete(`/bands/${band.slug}`)
    }
  }

  return (
    <>
      <Head title={band.name}>
        <meta name="description" content={band.content?.slice(0, 160) ?? `${band.name} — Australian Music Database.`} />
        <meta property="og:title" content={band.name} />
        <meta property="og:description" content={band.content?.slice(0, 160) ?? `${band.name} — Australian Music Database.`} />
        <meta property="og:type" content="music.band" />
        {band.primary_image?.og_image_url && <meta property="og:image" content={band.primary_image.og_image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      <PublicShell>
        {/* Hero + header */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-6 mb-8">
          <ImageGallery
            images={band.images ?? []}
            emptyIcon={<Music2 className="w-14 h-14 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 flex-wrap mb-2">
              <h1
                className="text-2xl sm:text-3xl font-bold text-stone-900 dark:text-white leading-tight tracking-tight"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                {band.name}
              </h1>
              {band.in_amh && (
                <span className="mt-1 sm:mt-1.5 inline-flex items-center text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full shrink-0 leading-none">
                  AMH feature
                </span>
              )}
            </div>

            {(band.years_active || band.home_town) && (
              <p className="text-sm text-stone-700 dark:text-stone-300 mb-2">
                {[band.years_active, band.home_town].filter(Boolean).join(" · ")}
              </p>
            )}

            <div className="flex items-center gap-1 text-xs text-stone-500 dark:text-stone-400 mb-4" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
              <span>{band.members_count} members</span>
              <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
              <span>{band.recordings_count} recordings</span>
              <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
              <span>{band.credits_count} credits</span>
              <span className="mx-1 text-stone-300 dark:text-stone-700">·</span>
              <span>{band.gigs_count} gigs</span>
            </div>

            {band.content && (
              <div
                className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed mb-4 [&_p]:mb-2 [&_p:last-child]:mb-0"
                dangerouslySetInnerHTML={{ __html: band.content }}
              />
            )}

            {permissions.can_edit && (
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  href={`/bands/${band.slug}/edit`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
                >
                  <Edit className="w-3.5 h-3.5" />
                  Edit
                </Link>
                {permissions.can_delete && (
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/60 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}
                {permissions.can_request_deletion && (
                  <RequestDeletionButton type="Band" id={band.id} name={band.name} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-stone-200 dark:border-stone-800 mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <div className="flex gap-0 -mb-px overflow-x-auto">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={[
                  "px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors focus-visible:outline-none",
                  activeTab === tab.id
                    ? "border-amber-500 text-amber-600 dark:text-amber-400"
                    : "border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:border-stone-300 dark:hover:border-stone-600",
                ].join(" ")}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Members */}
        {activeTab === "members" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/members/new?band_id=${band.id}&return_to=/bands/${band.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add member
                </Link>
              </div>
            )}
            {band.members.length === 0 ? (
              <EmptyState message="No members on record yet." />
            ) : (
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                {band.members.map((member) => (
                  <div
                    key={member.id}
                    className="group bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150"
                  >
                    <Link href={`/people/${member.person_slug}`} className="block no-underline">
                      <div className="aspect-square bg-stone-100 dark:bg-stone-800 overflow-hidden">
                        {member.person_image ? (
                          <img src={member.person_image.url} alt={member.person_image.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <UserRound className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="text-sm font-semibold text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                          {member.person_name}
                        </div>
                        {member.instruments.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {member.instruments.map((inst) => (
                              <span key={inst} className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 leading-none">
                                {inst}
                              </span>
                            ))}
                          </div>
                        )}
                        {(member.joined_year || member.left_year) && (
                          <div className="text-xs text-stone-500 dark:text-stone-400 mt-1.5 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                            {member.joined_year ?? "?"}{member.left_year ? `–${member.left_year}` : ""}
                          </div>
                        )}
                        {member.details && (
                          <div className="text-xs text-stone-500 dark:text-stone-400 mt-1 leading-snug" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }} title={member.details}>
                            {member.details.length > 80 ? member.details.slice(0, 80) + "…" : member.details}
                          </div>
                        )}
                      </div>
                    </Link>
                    {permissions.can_edit && (
                      <div className="flex gap-3 px-3 pb-2.5 pt-0 border-t border-stone-100 dark:border-stone-800">
                        <Link
                          href={`/members/${member.id}/edit?return_to=/bands/${band.slug}`}
                          className="text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline pt-2"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm(`Remove ${member.person_name} from ${band.name}?`)) {
                              router.delete(`/members/${member.id}`, { data: { return_to: `/bands/${band.slug}` } })
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors pt-2"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recordings */}
        {activeTab === "recordings" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/recordings/new?recordable_type=Band&recordable_id=${band.id}&return_to=/bands/${band.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add recording
                </Link>
              </div>
            )}
            {band.recordings.length === 0 ? (
              <EmptyState message="No recordings on record yet." />
            ) : (
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                {band.recordings.map((rec) => (
                  <div
                    key={rec.id}
                    className="group bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150"
                  >
                    <Link href={`/recordings/${rec.slug}`} className="block no-underline">
                      <div className="aspect-square bg-stone-100 dark:bg-stone-800 overflow-hidden">
                        {rec.primary_image ? (
                          <img src={rec.primary_image.url} alt={rec.primary_image.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Disc className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="text-sm font-semibold text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                          {rec.title}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {rec.recording_type && (
                            <span className="text-xs px-1.5 py-px rounded bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 leading-none border border-stone-200 dark:border-stone-700">
                              {rec.recording_type}
                            </span>
                          )}
                          {rec.released_year && (
                            <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                              {rec.released_year}
                            </span>
                          )}
                        </div>
                        {rec.highest_position !== null && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <span
                              className={`text-xs font-bold tabular-nums ${rec.highest_position === 1 ? "text-amber-500" : "text-stone-500 dark:text-stone-400"}`}
                              style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                            >
                              #{rec.highest_position}
                            </span>
                            {rec.weeks_in_chart && (
                              <span className="text-xs text-stone-500 dark:text-stone-400 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                                · {rec.weeks_in_chart}wks
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                    {permissions.can_edit && (
                      <div className="flex gap-3 px-3 pb-2.5 pt-0 border-t border-stone-100 dark:border-stone-800">
                        <Link
                          href={`/recordings/${rec.slug}/edit?return_to=/bands/${band.slug}`}
                          className="text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline pt-2"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm(`Delete "${rec.title}"? This cannot be undone.`)) {
                              router.delete(`/recordings/${rec.slug}`, { data: { return_to: `/bands/${band.slug}` } })
                            }
                          }}
                          className="text-xs text-red-400 hover:text-red-600 transition-colors pt-2"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Credits */}
        {activeTab === "credits" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/credits/new?creditable_type=Band&creditable_id=${band.id}&return_to=/bands/${band.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add credit
                </Link>
              </div>
            )}
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
              {band.credits.map((credit) => (
                <div
                  key={credit.id}
                  className="group bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150"
                >
                  <Link href={`/people/${credit.person_slug}`} className="block no-underline">
                    <div className="aspect-square bg-stone-100 dark:bg-stone-800 overflow-hidden">
                      {credit.person_image ? (
                        <img src={credit.person_image.url} alt={credit.person_image.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <UserRound className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-semibold text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                        {credit.person_name}
                      </div>
                      <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 leading-none mt-1.5">
                        {credit.role}
                      </span>
                      {credit.details && (
                        <p className="text-xs text-stone-400 dark:text-stone-500 mt-1 truncate">{credit.details}</p>
                      )}
                    </div>
                  </Link>
                  {permissions.can_edit && (
                    <div className="flex gap-3 px-3 pb-2.5 pt-0 border-t border-stone-100 dark:border-stone-800">
                      <Link
                        href={`/credits/${credit.id}/edit?return_to=/bands/${band.slug}`}
                        className="text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline pt-2"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => {
                          if (confirm(`Delete credit for ${credit.person_name}?`)) {
                            router.delete(`/credits/${credit.id}`, { data: { return_to: `/bands/${band.slug}` } })
                          }
                        }}
                        className="text-xs text-red-400 hover:text-red-600 transition-colors pt-2"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gigs */}
        {activeTab === "gigs" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4 flex justify-end">
                <Link href={`/gigs/new?gigable_type=Band&gigable_id=${band.id}&return_to=/bands/${band.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline">
                  <Plus className="w-3.5 h-3.5" />
                  Add gig
                </Link>
              </div>
            )}
            <GigsTab gigs={band.gigs} canEdit={permissions.can_edit} returnTo={`/bands/${band.slug}`} />
          </div>
        )}

        {/* Videos */}
        {activeTab === "videos" && (
          <div>
            {permissions.can_edit && (
              <div className="mb-4">
                <Link
                  href={`/videos/new?pre_attach_type=Band&pre_attach_id=${band.id}&return_to=${encodeURIComponent(`/bands/${band.slug}`)}`}
                  className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add video
                </Link>
              </div>
            )}
            {band.videos.length === 0 ? (
              <EmptyState message="No videos on record yet." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {band.videos.map((video) => (
                  <div key={video.id} className="rounded-xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
                    <div className="aspect-video w-full">
                      <iframe
                        src={videoEmbedUrl(video.url)}
                        title={video.name}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="px-3.5 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-stone-700 dark:text-stone-300 truncate block" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                            {video.name}
                          </span>
                          {video.source !== "direct" && (
                            <span className="text-xs text-stone-500 dark:text-stone-400 truncate block" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                              from {video.source}
                            </span>
                          )}
                        </div>
                        {permissions.can_edit && video.source === "direct" && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Link
                              href={`/videos/${video.id}/edit?return_to=${encodeURIComponent(`/bands/${band.slug}`)}`}
                              className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                            >
                              <Edit className="w-3 h-3" /> Edit
                            </Link>
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm("Delete this video?")) {
                                  router.delete(`/videos/${video.id}`, { data: { return_to: `/bands/${band.slug}` } })
                                }
                              }}
                              className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Links */}
        {activeTab === "links" && (
          <div>
            {band.links.length === 0 && !permissions.can_edit ? (
              <EmptyState message="No links on record yet." />
            ) : (
              <div className="divide-y divide-stone-100 dark:divide-stone-800/80">
                {band.links.map((link) => (
                  <div key={link.id} className="flex items-center gap-3.5 py-3.5 group/lnk">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3.5 flex-1 min-w-0 no-underline group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0 group-hover:bg-amber-50 dark:group-hover:bg-amber-950/40 border border-stone-200 dark:border-stone-700 group-hover:border-amber-200 dark:group-hover:border-amber-800 transition-colors">
                        <ExternalLink className="w-3.5 h-3.5 text-stone-400 group-hover:text-amber-500 transition-colors" />
                      </div>
                      <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug flex-1">
                        {link.link_text}
                      </span>
                      <ChevronRight className="w-4 h-4 text-stone-300 dark:text-stone-600 shrink-0" />
                    </a>
                    {permissions.can_edit && (
                      <div className="flex items-center gap-1 opacity-0 group-hover/lnk:opacity-100 transition-opacity shrink-0">
                        <Link href={`/links/${link.id}/edit?return_to=/bands/${band.slug}`} className="p-1 rounded text-stone-400 hover:text-amber-600 no-underline" title="Edit"><Edit className="w-3.5 h-3.5" /></Link>
                        <button type="button" onClick={() => { if (confirm("Delete this link?")) router.delete(`/links/${link.id}`, { data: { return_to: `/bands/${band.slug}` } }) }} className="p-1 rounded text-stone-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {permissions.can_edit && (
              <div className="mt-4">
                <Link href={`/links/new?linkable_type=Band&linkable_id=${band.id}&return_to=/bands/${band.slug}`} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-600 no-underline">
                  <Plus className="w-3.5 h-3.5" />Add link
                </Link>
              </div>
            )}
          </div>
        )}

        {activeTab === "photos" && (
          <PhotosTab
            images={band.images}
            adminControls={permissions.can_edit ? {
              setPrimaryBasePath: "/admin/image_attachments",
              removeBasePath:     "/admin/image_attachments",
              deleteBasePath:     "/admin/image_attachments",
              addImageHref:       `/admin/images/new?attachable_type=Band&attachable_id=${band.id}&return_to=/bands/${band.slug}`,
              attachExistingHref: `/admin/image_attachments/new?attachable_type=Band&attachable_id=${band.id}&return_to=/bands/${band.slug}`,
              returnTo:           `/bands/${band.slug}`,
            } : undefined}
          />
        )}

        {permissions.can_edit && (
          <ResearchAssistantPanel entityType="Band" entityId={band.id} />
        )}
      </PublicShell>
    </>
  )
}
